import { memo, useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid, ToggleButton, ToggleButtonGroup, styled } from '@mui/material';
import SizeDisplay, { type SizeDisplaySourceUnit } from '../../Common/Measurements/SizeDisplay';

const SIZE_SCALE = 12;
const MOBILE_SIZE_SCALE = 8.5;

export type PrintOrientation = 'landscape' | 'portrait';

export interface PrintSize {
  width: number
  height: number
  tone: number
}

type PrintSizeSourceUnit = Extract<SizeDisplaySourceUnit, 'in' | 'cm'>;

interface Props {
  defaultOrientation?: PrintOrientation
  orientedSizes: Record<PrintOrientation, PrintSize[]>
  sourceUnit?: PrintSizeSourceUnit
  squareSizes?: PrintSize[]
}

interface PrintSizeSlotProps {
  heightUnits: number
  widthUnits: number
}

interface PrintBoxOptions {
  reactKey?: string
  stacked?: boolean
  stackZIndex?: number
}

const getPrintSizeKey = (size: PrintSize) => {
  return `${size.width}x${size.height}`;
}

const SizeModule = styled('div')(({ theme }) => ({
  margin: '48px auto 0',
  maxWidth: 960,
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(4),
  },
}));

const OrientationToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: '#464646',
  borderRadius: 999,
  marginBottom: theme.spacing(5),
  padding: 3,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.short,
  }),

  '&:hover': {
    backgroundColor: '#616161',
  },

  '.MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: 999,
    margin: 0,

    '&:not(:first-of-type)': {
      borderRadius: 999,
    },

    '&:first-of-type': {
      borderRadius: 999,
    },
  },
}));

const OrientationButton = styled(ToggleButton)(({ theme }) => ({
  color: '#fff',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '0.95rem',
  fontWeight: 500,
  lineHeight: 1.2,
  minWidth: 120,
  padding: theme.spacing(1, 2.5),
  textTransform: 'uppercase',

  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: '#758193',
    color: '#fff',
  },
}));

const SizeVisualizer = styled(Grid)(({ theme }) => ({
  alignItems: 'flex-end',
  justifyContent: 'center',
  minHeight: 236,

  [theme.breakpoints.down('sm')]: {
    minHeight: 0,
  },
}));

const SizeGridItem = styled(Grid)(() => ({
  alignItems: 'flex-end',
  display: 'flex',
  justifyContent: 'center',
}));

const SizeStack = styled('div', {
  shouldForwardProp: prop => !['heightUnits', 'widthUnits'].includes(String(prop)),
})<PrintSizeSlotProps>(({ theme, heightUnits, widthUnits }) => ({
  flex: '0 0 auto',
  height: Math.round(heightUnits * SIZE_SCALE),
  position: 'relative',
  width: Math.round(widthUnits * SIZE_SCALE),

  [theme.breakpoints.down('sm')]: {
    height: Math.round(heightUnits * MOBILE_SIZE_SCALE),
    width: Math.round(widthUnits * MOBILE_SIZE_SCALE),
  },
}));

const SizeSlot = styled('div', {
  shouldForwardProp: prop => !['heightUnits', 'widthUnits'].includes(String(prop)),
})<PrintSizeSlotProps>(({ theme, heightUnits, widthUnits }) => ({
  alignItems: 'flex-end',
  display: 'flex',
  height: Math.round(heightUnits * SIZE_SCALE),
  justifyContent: 'center',
  width: Math.round(widthUnits * SIZE_SCALE),

  [theme.breakpoints.down('sm')]: {
    height: Math.round(heightUnits * MOBILE_SIZE_SCALE),
    width: Math.round(widthUnits * MOBILE_SIZE_SCALE),
  },
}));

const PrintBox = styled('div', {
  shouldForwardProp: prop => !['heightUnits', 'stacked', 'stackZIndex', 'tone', 'widthUnits'].includes(String(prop)),
})<{
  heightUnits: number
  stacked?: boolean
  stackZIndex?: number
  tone: number
  widthUnits: number
}>(({ theme, heightUnits, stacked, stackZIndex, tone, widthUnits }) => {
  const shades = ['#979797', '#7a7a7a', '#616161', '#555', '#464646'];
  const height = Math.round(heightUnits * SIZE_SCALE);
  const mobileHeight = Math.round(heightUnits * MOBILE_SIZE_SCALE);
  const mobileWidth = Math.round(widthUnits * MOBILE_SIZE_SCALE);
  const width = Math.round(widthUnits * SIZE_SCALE);

  return {
    alignItems: 'flex-end',
    backgroundColor: shades[tone],
    border: '1px solid #fff',
    boxSizing: 'border-box',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Poppins, sans-serif',
    justifyContent: 'flex-start',
    height,
    left: stacked ? 0 : undefined,
    maxWidth: '100%',
    minWidth: 128,
    padding: theme.spacing(1),
    position: stacked ? 'absolute' : 'relative',
    textAlign: 'right',
    bottom: stacked ? 0 : undefined,
    transformOrigin: 'bottom center',
    transition: theme.transitions.create(['background-color', 'height', 'width'], {
      duration: 500,
      easing: theme.transitions.easing.easeInOut,
    }),
    width,
    willChange: 'height, width',
    zIndex: stackZIndex,

    [theme.breakpoints.down('sm')]: {
      height: mobileHeight,
      minWidth: 0,
      width: mobileWidth,
    },

    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },

    '.box-copy': {
      display: 'block',
      maxWidth: '100%',
      transition: theme.transitions.create(['opacity', 'transform'], {
        duration: theme.transitions.duration.short,
        easing: theme.transitions.easing.easeOut,
      }),
    },

    '.size-label': {
      display: 'block',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.1,
    },
  };
});

function getPrintSizeAt(sizes: PrintSize[], index: number): PrintSize | undefined {
  return index < sizes.length ? sizes[index] : undefined;
}

export default memo(function PrintSizeModule(props: Props) {
  const [t] = useTranslation('page/products/_sections');
  const [orientation, setOrientation] = useState<PrintOrientation>(props.defaultOrientation ?? 'landscape');
  const sourceUnit = props.sourceUnit ?? 'in';
  const squareSizes = props.squareSizes ?? [];
  const orientedSizeSlots = useMemo(() => {
    const slotCount = Math.max(props.orientedSizes.landscape.length, props.orientedSizes.portrait.length);

    return Array.from({ length: slotCount }, (_item, index): PrintSizeSlotProps => {
      const landscapeSize = getPrintSizeAt(props.orientedSizes.landscape, index);
      const portraitSize = getPrintSizeAt(props.orientedSizes.portrait, index);
      const sizes = [landscapeSize, portraitSize].filter((size): size is PrintSize => size !== undefined);

      return {
        heightUnits: Math.max(...sizes.map(size => size.height)),
        widthUnits: Math.max(...sizes.map(size => size.width)),
      };
    });
  }, [props.orientedSizes]);

  const handleOrientationChange = useCallback((_event: MouseEvent<HTMLElement>, nextOrientation: PrintOrientation | null) => {
    if (nextOrientation) {
      setOrientation(nextOrientation);
    }
  }, []);

  const renderPrintBox = useCallback((size: PrintSize, boxProps?: PrintBoxOptions) => {
    return <PrintBox
      key={boxProps?.reactKey ?? getPrintSizeKey(size)}
      heightUnits={size.height}
      stacked={boxProps?.stacked}
      stackZIndex={boxProps?.stackZIndex}
      tone={size.tone}
      widthUnits={size.width}
    >
      <span className="box-copy">
        <span className="size-label">
          <SizeDisplay sizes={[size.width, size.height]} sourceUnit={sourceUnit} displayType="physical" />
        </span>
      </span>
    </PrintBox>;
  }, [sourceUnit]);

  return <Container maxWidth="md">
    <SizeModule>
      <OrientationToggle
        exclusive
        value={orientation}
        onChange={handleOrientationChange}
        aria-label={t('Orientation')}
      >
        <OrientationButton value="landscape">{t('Landscape')}</OrientationButton>
        <OrientationButton value="portrait">{t('Portrait')}</OrientationButton>
      </OrientationToggle>
      <SizeVisualizer
        container
        columnSpacing={{
          xs: 2,
          sm: 2.5,
        }}
        rowSpacing={{
          xs: 2,
          sm: 2.5,
        }}
      >
        {squareSizes.length > 0 ? <SizeGridItem item xs={6} sm="auto">
          <SizeStack
            heightUnits={Math.max(...squareSizes.map(size => size.height))}
            widthUnits={Math.max(...squareSizes.map(size => size.width))}
          >
            {squareSizes.map((size, index) => renderPrintBox(size, {
              stacked: true,
              stackZIndex: squareSizes.length - index,
            }))}
          </SizeStack>
        </SizeGridItem> : null}
        {props.orientedSizes[orientation].map((size, index) => <SizeGridItem
          item
          xs={6}
          sm="auto"
          key={`oriented-slot-${index}`}
        >
          <SizeSlot
            heightUnits={orientedSizeSlots[index].heightUnits}
            widthUnits={orientedSizeSlots[index].widthUnits}
          >
            {renderPrintBox(size, {
              reactKey: `oriented-${index}`,
            })}
          </SizeSlot>
        </SizeGridItem>)}
      </SizeVisualizer>
    </SizeModule>
  </Container>;
})
